import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InstrumentSetupGate } from "../../../components/Instruments/InstrumentSetupGate";
import {
  getReport,
  listReportStrategyPolicyVersions,
  previewCombineReportStrategyPolicies,
} from "../../../services/reportsApi";
import {
  saveSimulateTradePolicyJob,
  simulateTradePolicyStart,
  waitForSimulateTradePolicyJob,
} from "../../../services/tradePolicyApi";
import type { Report } from "../../../types/reports";
import type {
  TradePolicyAction,
  TradePolicyGroupOp,
  TradePolicySimulatePolicy,
  TradePolicySimulateRequest,
} from "../../../types/tradePolicy";
import {
  isPolicyPayloadV1,
  isPolicyTreeBundleV1,
  isPolicyTreeV2,
} from "../../../types/tradePolicy";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type { WorkspaceCreateContext, WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { findUpstreamStrategyEntityId, workspaceNodeTitle } from "./workspaceGraphUtils";
import { normalizeMandatorySimulatorRunTitle } from "../ExecutionRulesSimulator/simulatorRunTitleFields";
import { MAX_COMPARISON_RUN_TITLE_LENGTH } from "../ExecutionRulesSimulator/simulatorComparisonTypes";

export type WorkspaceCombinedSimulationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: WorkspaceAccountScope;
  workspaceContext: WorkspaceCreateContext;
  combinedPolicyNode: WorkspaceNode;
  nodes: WorkspaceNode[];
  edges: WorkspaceEdge[];
  lockBrokerAccountCode: string;
  onSimulationSaved: (backtestId: string) => void;
};

const ACTION_OPTIONS: Array<{ value: TradePolicyAction; label: string }> = [
  { value: "veto", label: "Remove matching trades" },
  { value: "keep_only", label: "Keep only matching trades" },
];

const COMBINE_OPTIONS: Array<{ value: TradePolicyGroupOp; label: string }> = [
  { value: "and", label: "Combine AND" },
  { value: "or", label: "Combine OR" },
];

type PreparedCombinedSim = {
  policy: TradePolicySimulatePolicy;
  policyRowVersion?: string;
  report: Report;
  strategyId: string;
  initialAction: TradePolicyAction;
  initialCombine: TradePolicyGroupOp;
  /** Bundle / v2 trees keep segment structure; combine is display-only then. */
  combineEditable: boolean;
  actionEditable: boolean;
};

function ymdFromReportDate(raw: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw ?? "").trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function readInitialAction(policy: TradePolicySimulatePolicy): TradePolicyAction {
  if (isPolicyPayloadV1(policy)) {
    return policy.action === "keep_only" ? "keep_only" : "veto";
  }
  if (isPolicyTreeBundleV1(policy)) {
    const actions = policy.segments
      .map((seg) => seg.action)
      .filter((a): a is TradePolicyAction => a === "veto" || a === "keep_only");
    if (actions.length > 0 && actions.every((a) => a === actions[0])) {
      return actions[0]!;
    }
  }
  return "veto";
}

function readInitialCombine(policy: TradePolicySimulatePolicy): TradePolicyGroupOp {
  if (isPolicyPayloadV1(policy) && policy.root.kind === "group") {
    return policy.root.op === "or" ? "or" : "and";
  }
  return "and";
}

function applyRunOptionsToPolicy(
  policy: TradePolicySimulatePolicy,
  action: TradePolicyAction,
  combine: TradePolicyGroupOp
): TradePolicySimulatePolicy {
  if (isPolicyTreeV2(policy)) {
    return policy;
  }
  if (isPolicyTreeBundleV1(policy)) {
    return {
      ...policy,
      segments: policy.segments.map((segment) => ({ ...segment, action })),
    };
  }
  if (isPolicyPayloadV1(policy)) {
    const root =
      policy.root.kind === "group" ? { ...policy.root, op: combine } : policy.root;
    return { action, root };
  }
  return policy;
}

function buildWindowFieldsFromReport(
  report: Report
): Pick<
  TradePolicySimulateRequest,
  | "local_start_date"
  | "local_end_date"
  | "timeframe"
  | "timezone"
  | "reporting_timezone"
  | "symbol"
  | "candle_timeframe"
> {
  const localStart =
    ymdFromReportDate(report.cohort_period?.local_start_date) ??
    ymdFromReportDate(report.start_date);
  const localEnd =
    ymdFromReportDate(report.cohort_period?.local_end_date) ??
    ymdFromReportDate(report.end_date);
  const timezone = report.timezone?.trim() || report.reporting_timezone?.trim() || undefined;
  const timeframe = report.timeframe?.trim() || undefined;
  const symbol = report.symbol?.trim() || undefined;
  const candle = report.candle_timeframe?.trim();
  const candle_timeframe =
    candle === "1m" ||
    candle === "5m" ||
    candle === "15m" ||
    candle === "30m" ||
    candle === "1h" ||
    candle === "4h"
      ? candle
      : undefined;

  if (localStart && localEnd) {
    return {
      local_start_date: localStart,
      local_end_date: localEnd,
      ...(timezone ? { timezone, reporting_timezone: timezone } : {}),
      ...(symbol ? { symbol } : {}),
      ...(candle_timeframe && candle_timeframe !== "1m" ? { candle_timeframe } : {}),
    };
  }

  return {
    ...(timeframe ? { timeframe } : { timeframe: "all-time" }),
    ...(timezone ? { timezone, reporting_timezone: timezone } : {}),
    ...(symbol ? { symbol } : {}),
    ...(candle_timeframe && candle_timeframe !== "1m" ? { candle_timeframe } : {}),
  };
}

/**
 * Slim combined-sim run: combined policy + adopted report window are fixed.
 * User sets title, action, and combine mode, then runs.
 */
export function WorkspaceCombinedSimulationDialog({
  open,
  onOpenChange,
  scope: _scope,
  workspaceContext,
  combinedPolicyNode,
  nodes,
  edges,
  lockBrokerAccountCode,
  onSimulationSaved,
}: WorkspaceCombinedSimulationDialogProps): React.ReactElement {
  const adoptedReportId = combinedPolicyNode.meta?.adoptedReportId?.trim() || null;
  const sourcePolicyVersionIds = combinedPolicyNode.meta?.sourcePolicyVersionIds ?? [];

  const strategyId = useMemo(
    () => findUpstreamStrategyEntityId(combinedPolicyNode.id, nodes, edges),
    [combinedPolicyNode.id, edges, nodes]
  );

  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  const [prepared, setPrepared] = useState<PreparedCombinedSim | null>(null);
  const [title, setTitle] = useState("");
  const [action, setAction] = useState<TradePolicyAction>("veto");
  const [combine, setCombine] = useState<TradePolicyGroupOp>("and");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setGateError(null);
    setPrepared(null);
    setTitle("");
    setAction("veto");
    setCombine("and");
    setRunning(false);

    void (async () => {
      try {
        if (!adoptedReportId) {
          throw new Error(
            "This combined policy has no adopted report window. Recreate it with a first-selected source policy that has a report."
          );
        }
        if (!strategyId) {
          throw new Error("Could not resolve the strategy for this combined policy.");
        }

        if (sourcePolicyVersionIds.length >= 2) {
          const preview = await previewCombineReportStrategyPolicies(strategyId, {
            policy_version_ids: sourcePolicyVersionIds,
          });
          const errors = Array.isArray(preview.errors) ? preview.errors : [];
          const invalid =
            preview.ok === false || preview.valid === false || errors.length > 0;
          if (invalid) {
            throw new Error(
              errors[0]?.message ||
                "Source policies no longer pass overlap / combine checks. Update the combined policy first."
            );
          }
        }

        const [versionsResponse, report] = await Promise.all([
          listReportStrategyPolicyVersions(strategyId, { include_archived: true }),
          getReport(adoptedReportId),
        ]);
        if (cancelled) return;

        const match =
          (versionsResponse.versions ?? []).find(
            (row) => String(row.id) === String(combinedPolicyNode.entityId)
          ) ?? null;
        if (!match?.policy_tree) {
          throw new Error("Combined policy version not found or has no policy tree.");
        }

        const policy = match.policy_tree as TradePolicySimulatePolicy;
        const isClassic = isPolicyPayloadV1(policy);
        const isBundle = isPolicyTreeBundleV1(policy);
        const isV2 = isPolicyTreeV2(policy);
        if (!isClassic && !isBundle && !isV2) {
          throw new Error("Unsupported combined policy tree shape.");
        }

        const initialAction = readInitialAction(policy);
        const initialCombine = readInitialCombine(policy);
        const next: PreparedCombinedSim = {
          policy,
          policyRowVersion:
            (typeof match.policy_row_version === "string" && match.policy_row_version.trim()) ||
            (typeof match.policy_schema_version === "string" && match.policy_schema_version.trim()) ||
            undefined,
          report,
          strategyId,
          initialAction,
          initialCombine,
          actionEditable: isClassic || isBundle,
          combineEditable: isClassic && policy.root.kind === "group",
        };
        setPrepared(next);
        setAction(initialAction);
        setCombine(initialCombine);
        const nodeTitle =
          combinedPolicyNode.meta?.title?.trim() ||
          workspaceNodeTitle(combinedPolicyNode).trim();
        setTitle(nodeTitle ? `${nodeTitle} sim` : "Combined simulation");
      } catch (err) {
        if (cancelled) return;
        setGateError(
          err instanceof Error ? err.message : "Failed to prepare combined simulation."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    adoptedReportId,
    combinedPolicyNode.entityId,
    combinedPolicyNode.meta?.title,
    combinedPolicyNode,
    open,
    sourcePolicyVersionIds,
    strategyId,
  ]);

  const normalizedTitle = normalizeMandatorySimulatorRunTitle(title);
  const canRun =
    Boolean(prepared) &&
    Boolean(normalizedTitle) &&
    !loading &&
    !running &&
    !gateError;

  const onRun = useCallback(async () => {
    if (!prepared) return;
    const runTitle = normalizeMandatorySimulatorRunTitle(title);
    if (!runTitle) {
      toast.info("Enter a run title", {
        description: "Add a title before running. It appears on the canvas and in results.",
      });
      return;
    }

    setRunning(true);
    setGateError(null);

    try {
      const policy = applyRunOptionsToPolicy(prepared.policy, action, combine);
      const windowFields = buildWindowFieldsFromReport(prepared.report);
      const broker =
        lockBrokerAccountCode.trim() ||
        String(prepared.report.broker_account_code ?? "").trim();

      const body: TradePolicySimulateRequest = {
        policy,
        ...(prepared.policyRowVersion
          ? { policy_row_version: prepared.policyRowVersion }
          : {}),
        simulate_ohlc_fill_missing: false,
        ...(broker ? { broker_account_code: broker } : {}),
        ...windowFields,
        include_trade_ids: true,
        limit: 5000,
        report_id: prepared.report.id,
        strategy_id: prepared.strategyId,
        title: runTitle,
      };

      const started = await simulateTradePolicyStart(body);
      let jobId: string | null = null;
      let backtestId: string | null = null;

      if (started && typeof started === "object" && "job_id" in started) {
        jobId = String((started as { job_id: string }).job_id);
        const outcome = await waitForSimulateTradePolicyJob(jobId);
        backtestId = outcome.backtestId ?? null;
      } else if (started && typeof started === "object" && "backtest_id" in started) {
        backtestId = String((started as { backtest_id?: string }).backtest_id ?? "");
      }

      if (jobId) {
        const saved = await saveSimulateTradePolicyJob(jobId, {
          workspaceId: workspaceContext.workspaceId,
          parentNodeId: workspaceContext.parentNodeId,
          ...(workspaceContext.position
            ? { position: workspaceContext.position }
            : {}),
          ...(workspaceContext.meta ? { meta: workspaceContext.meta } : {}),
        });
        if (!saved.ok) {
          throw new Error(
            saved.detail || "Simulation finished but could not save to the workspace."
          );
        }
        if (saved.backtestId) backtestId = saved.backtestId;
      }

      if (!backtestId) {
        throw new Error("Simulation finished but no backtest id was returned.");
      }

      onSimulationSaved(backtestId);
      onOpenChange(false);
    } catch (err) {
      presentMutationError(err, "Combined simulation failed");
      setGateError(err instanceof Error ? err.message : "Combined simulation failed.");
    } finally {
      setRunning(false);
    }
  }, [
    action,
    combine,
    lockBrokerAccountCode,
    onOpenChange,
    onSimulationSaved,
    prepared,
    title,
    workspaceContext,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-workspace-combined-simulation-dialog=""
        className="max-w-md gap-4 font-[family-name:var(--font-primary)]"
      >
        <DialogHeader>
          <DialogTitle className="text-[color:var(--brand-tropical-surf)]">
            Run combined simulation
          </DialogTitle>
          <DialogDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
            Uses this combined policy against the adopted report’s trade window. Set a title,
            action, and combine mode, then run.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" aria-hidden />
            <span className="text-sm">Preparing combined simulation…</span>
          </div>
        ) : null}

        {gateError ? (
          <Alert>
            <AlertDescription className="text-[color:var(--brand-glacial-red)]">
              {gateError}
            </AlertDescription>
          </Alert>
        ) : null}

        {!loading && prepared ? (
          <InstrumentSetupGate featureLabel="Simulator">
            <div className="flex flex-col gap-4">
              <Field>
                <FieldLabel
                  htmlFor="workspace-combined-sim-title"
                  className="text-[color:var(--brand-tropical-surf)]"
                >
                  Name
                </FieldLabel>
                <Input
                  id="workspace-combined-sim-title"
                  value={title}
                  onChange={(event) =>
                    setTitle(event.target.value.slice(0, MAX_COMPARISON_RUN_TITLE_LENGTH))
                  }
                  placeholder={`Required. Max ${MAX_COMPARISON_RUN_TITLE_LENGTH} characters.`}
                  maxLength={MAX_COMPARISON_RUN_TITLE_LENGTH}
                  disabled={running}
                  required
                  aria-required
                />
              </Field>

              <Field>
                <FieldLabel
                  htmlFor="workspace-combined-sim-action"
                  className="text-[color:var(--brand-tropical-surf)]"
                >
                  Action
                </FieldLabel>
                <Select
                  value={action}
                  onValueChange={(next) => {
                    if (next === "veto" || next === "keep_only") setAction(next);
                  }}
                  disabled={running || !prepared.actionEditable}
                >
                  <SelectTrigger id="workspace-combined-sim-action" className="w-full">
                    <SelectValue placeholder="Select action" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!prepared.actionEditable ? (
                  <FieldDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
                    This policy type locks action at simulate time.
                  </FieldDescription>
                ) : null}
              </Field>

              <Field>
                <FieldLabel
                  htmlFor="workspace-combined-sim-combine"
                  className="text-[color:var(--brand-tropical-surf)]"
                >
                  Combine
                </FieldLabel>
                <Select
                  value={combine}
                  onValueChange={(next) => {
                    if (next === "and" || next === "or") setCombine(next);
                  }}
                  disabled={running || !prepared.combineEditable}
                >
                  <SelectTrigger id="workspace-combined-sim-combine" className="w-full">
                    <SelectValue placeholder="Select combine mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMBINE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!prepared.combineEditable ? (
                  <FieldDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
                    Combined bundles keep each source segment separate; AND/OR applies to classic
                    trees only.
                  </FieldDescription>
                ) : null}
              </Field>
            </div>
          </InstrumentSetupGate>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            data-font="primary"
            disabled={running}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="gradient"
            data-font="primary"
            disabled={!canRun}
            onClick={() => void onRun()}
          >
            {running ? <Loader2 className="size-4 animate-spin" /> : null}
            {running ? "Running…" : "Run"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Soft guard used by callers before opening the dialog. */
export function toastIfMissingAdoptedReport(node: WorkspaceNode): boolean {
  if (node.meta?.adoptedReportId?.trim()) return true;
  toast.warning("Missing adopted report", {
    description:
      "This combined policy has no report window. Combine policies again from a report-linked source.",
  });
  return false;
}
